/**
 * "Show Sample workspace" toggle. Default ON — every portal opens with
 * the Sample workspace group visible alongside "Your portal" so a fresh
 * lawyer sees what populated surfaces are supposed to look like before
 * they connect their own data.
 *
 *   - Inside a `/c/${slug}` portal: default flips to the portal's
 *     `tenantBranding.showDemo` flag (backend field name retained for
 *     compat) the first time we render for that slug. Subsequent
 *     navigations honour the user's per-slug localStorage choice.
 *   - Persists per-slug (and one bucket for non-portal users) so
 *     flipping it off inside one portal doesn't blank the default `/`
 *     desktop or another firm's portal.
 *
 * F19.6 signed-in short-circuit: when Clerk identity is non-null, the
 * hook returns `[false, noop]` and the `setter` is a no-op. The demo
 * path is signed-out-only — signed-in users have a real `users` row +
 * portal membership rows (F19.1 / F19.2) and the showcase belongs to
 * the unauthenticated trial flow only. Skipping the localStorage read
 * also means no per-user-Clerk `showcaseSessionId` ever reaches Convex
 * (call sites guard on this hook's boolean before injecting the
 * `demoSessionId` arg-key).
 *
 * Storage key:  repancy:showcaseVisible[:slug]
 * Migration:    legacy `repancy:showDemo[:slug]` keys are read once and
 *               rewritten under the new prefix.
 * Listeners:    `storage` events so two tabs of the same portal stay in
 *               sync without a reload.
 */

import { useCallback, useEffect, useState } from 'react';
import { useUser } from '@clerk/react';
import { useOptionalTenantBranding } from '../tenant/TenantBrandingContext';

const KEY_PREFIX = 'repancy:showcaseVisible';
const LEGACY_KEY_PREFIX = 'repancy:showDemo';

function storageKey(slug: string | null): string {
  return slug === null ? KEY_PREFIX : `${KEY_PREFIX}:${slug}`;
}
function legacyStorageKey(slug: string | null): string {
  return slug === null ? LEGACY_KEY_PREFIX : `${LEGACY_KEY_PREFIX}:${slug}`;
}

function migrateLegacy(slug: string | null): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const legacy = window.localStorage.getItem(legacyStorageKey(slug));
    if (legacy === null) return null;
    window.localStorage.setItem(storageKey(slug), legacy);
    window.localStorage.removeItem(legacyStorageKey(slug));
    return legacy;
  } catch {
    return null;
  }
}

function read(slug: string | null, fallback: boolean): boolean {
  if (typeof window === 'undefined') {return fallback;}
  try {
    const migrated = migrateLegacy(slug);
    const raw = migrated ?? window.localStorage.getItem(storageKey(slug));
    if (raw === '1') {return true;}
    if (raw === '0') {return false;}
  } catch {
    // ignore — privacy mode / SSR
  }
  return fallback;
}

function write(slug: string | null, value: boolean): void {
  if (typeof window === 'undefined') {return;}
  try {
    window.localStorage.setItem(storageKey(slug), value ? '1' : '0');
  } catch {
    // ignore
  }
}

const NOOP_SETTER = (_next: boolean): void => {
  // F19.6 — signed-in callers can't toggle the showcase. Swallow the
  // write rather than throw so existing toggle UIs that happen to be
  // rendered (e.g. an admin viewing a portal) degrade quietly.
  void _next;
};

/**
 * F19.6 — pure decision helper. Extracted so the hook can be exercised
 * by unit tests without a Clerk provider tree.
 *
 *   - `clerkLoaded === false`: identity unknown — fall through to the
 *     storage value so we don't flicker the showcase off during the
 *     Clerk hydration window on a hard refresh.
 *   - `clerkLoaded === true && clerkUserId !== null`: signed-in user
 *     — short-circuit to `false`. The showcase is signed-out-only.
 *   - otherwise: signed-out — return the storage value.
 */
export function resolveShowcaseVisibility(opts: {
  clerkLoaded: boolean;
  clerkUserId: string | null;
  storageValue: boolean;
}): { visible: boolean; signedIn: boolean } {
  const signedIn = opts.clerkLoaded && opts.clerkUserId !== null;
  if (signedIn) return { visible: false, signedIn: true };
  return { visible: opts.storageValue, signedIn: false };
}

export function useShowcaseVisible(): readonly [boolean, (next: boolean) => void] {
  const portal = useOptionalTenantBranding();
  const slug = portal?.slug ?? null;
  const fallback = portal?.branding.showDemo ?? true;

  // F19.6 — Clerk identity short-circuit. `useUser` always returns a
  // stable object; `isLoaded === false` means Clerk hasn't hydrated
  // yet so we MUST NOT short-circuit (would briefly hide the
  // showcase on a hard refresh while Clerk fetches the session).
  const { user, isLoaded } = useUser();

  const [value, setValue] = useState<boolean>(() => read(slug, fallback));

  useEffect(() => {
    setValue(read(slug, fallback));
  }, [slug, fallback]);

  useEffect(() => {
    if (typeof window === 'undefined') {return;}
    const handler = (e: StorageEvent) => {
      if (e.key !== storageKey(slug)) {return;}
      setValue(read(slug, fallback));
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [slug, fallback]);

  const set = useCallback(
    (next: boolean) => {
      write(slug, next);
      setValue(next);
    },
    [slug],
  );

  const resolved = resolveShowcaseVisibility({
    clerkLoaded: isLoaded,
    clerkUserId: user?.id ?? null,
    storageValue: value,
  });
  if (resolved.signedIn) return [false, NOOP_SETTER] as const;
  return [resolved.visible, set] as const;
}

// Test-only helper: useful for vitest setup/teardown to start each
// test in a clean state.
export function _clearShowcaseVisibleStorage(slug: string | null): void {
  if (typeof window === 'undefined') {return;}
  try {
    window.localStorage.removeItem(storageKey(slug));
    window.localStorage.removeItem(legacyStorageKey(slug));
  } catch {
    // ignore
  }
}
